import React, { useMemo, useRef, useState, createContext, useContext, useEffect } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

// Performance context for managing animation states
const PerformanceContext = createContext();

function PerformanceProvider({ children }) {
  const [isInteracting, setIsInteracting] = useState(false);
  const [lastInteractionTime, setLastInteractionTime] = useState(0);
  
  return (
    <PerformanceContext.Provider value={{ 
      isInteracting, 
      setIsInteracting,
      lastInteractionTime,
      setLastInteractionTime 
    }}>
      {children}
    </PerformanceContext.Provider>
  );
}

const usePerformance = () => useContext(PerformanceContext);

// Soft outline mesh creator
function OutlineMesh({ geometry, color = "#F5DEB3", scale = 1.015 }) {
  return (
    <mesh geometry={geometry} scale={scale}>
      <meshBasicMaterial color={color} side={THREE.BackSide} />
    </mesh>
  );
}


// Character component
function Character() {
  const groupRef = useRef();
  const leftLegGroupRef = useRef();
  const rightLegGroupRef = useRef();
  const leftArmGroupRef = useRef();
  const rightArmGroupRef = useRef();
  const torsoRef = useRef();
  const { isInteracting } = usePerformance();
  
  // Create low-poly hair geometry
  const hairGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    
    // Define vertices for complete low-poly hair mesh
    const vertices = new Float32Array([
      // Front hairline vertices (lowered to sit on head)
      0, 0.45, 0.62,      // 0 - top front center
      -0.25, 0.35, 0.63,  // 1 - left front
      0.25, 0.35, 0.63,   // 2 - right front
      0, 0.15, 0.65,      // 3 - front hairline center
      -0.35, 0.2, 0.6,    // 4 - left front side
      0.35, 0.2, 0.6,     // 5 - right front side
      
      // Top crown vertices (lowered to natural height)
      0, 0.65, 0,         // 6 - crown peak (lowered)
      -0.4, 0.55, 0.25,   // 7 - left crown
      0.4, 0.55, 0.25,    // 8 - right crown
      0, 0.58, -0.4,      // 9 - back crown
      -0.3, 0.58, 0.1,    // 10 - left top
      0.3, 0.58, 0.1,     // 11 - right top
      
      // Side vertices (expanded for fuller coverage)
      -0.72, -0.15, 0.15, // 12 - left side bottom (wider)
      -0.7, 0.1, 0.2,     // 13 - left side mid-low (wider)
      -0.65, 0.35, 0.05,  // 14 - left side mid-high (wider)
      -0.58, 0.45, -0.2,  // 15 - left side high (wider)
      0.72, -0.15, 0.15,  // 16 - right side bottom (wider)
      0.7, 0.1, 0.2,      // 17 - right side mid-low (wider)
      0.65, 0.35, 0.05,   // 18 - right side mid-high (wider)
      0.58, 0.45, -0.2,   // 19 - right side high (wider)
      
      // Back vertices (straight nape hairline, outside head sphere)
      -0.5, 0.4, -0.62,    // 20 - left back high (moved out)
      0.5, 0.4, -0.62,     // 21 - right back high (moved out)
      -0.55, -0.18, -0.66, // 22 - left back corner (outside head)
      0.55, -0.18, -0.66,  // 23 - right back corner (outside head)
      -0.3, -0.18, -0.66,  // 24 - left back mid (outside head)
      0.3, -0.18, -0.66,   // 25 - right back mid (outside head)
      0, 0.35, -0.68,      // 26 - back center high (outside head)
      0, -0.18, -0.66,     // 27 - back bottom center (outside head)
      
      // Temple and sideburn area
      -0.5, -0.05, 0.45,  // 28 - left temple low
      0.5, -0.05, 0.45,   // 29 - right temple low
      -0.45, 0.15, 0.5,   // 30 - left temple high
      0.45, 0.15, 0.5,    // 31 - right temple high
      
      // Additional vertices for complete top coverage
      -0.15, 0.6, 0.35,   // 32 - front-top left
      0.15, 0.6, 0.35,    // 33 - front-top right
      0, 0.65, 0.15,      // 34 - top center front (matches crown)
      -0.25, 0.62, -0.2,  // 35 - top back left
      0.25, 0.62, -0.2,   // 36 - top back right
      
      // NEW vertices for complete top cap (wider for full coverage)
      -0.55, 0.52, 0,     // 37 - left mid-top (wider)
      0.55, 0.52, 0,      // 38 - right mid-top (wider)
      -0.4, 0.6, -0.4,    // 39 - left back-top (wider)
      0.4, 0.6, -0.4,     // 40 - right back-top (wider)
      0, 0.63, -0.3,      // 41 - center back-top
    ]);
    
    // Define faces (triangles) for complete mesh
    const indices = new Uint16Array([
      // Front hairline
      0, 1, 3,
      0, 3, 2,
      1, 4, 3,
      2, 3, 5,
      
      // Front to temple connections
      4, 30, 28,
      4, 28, 3,
      5, 3, 29,
      5, 29, 31,
      1, 30, 4,  // Fill gap
      2, 31, 5,  // Fill gap
      
      // Front to top
      0, 32, 1,
      0, 2, 33,
      32, 34, 33,
      32, 33, 0,
      
      // Top crown area
      34, 6, 10,
      34, 10, 32,
      34, 11, 6,
      34, 33, 11,
      6, 35, 10,
      6, 11, 36,
      6, 36, 35,
      6, 7, 10,   // Fill crown gap
      6, 8, 11,   // Fill crown gap
      6, 9, 35,   // Connect crown to back
      6, 9, 36,   // Connect crown to back
      
      // Left side complete coverage
      1, 32, 7,
      1, 7, 4,
      4, 7, 30,
      30, 7, 14,
      30, 14, 13,
      30, 13, 28,
      28, 13, 12,
      14, 15, 13,
      7, 10, 14,
      10, 15, 14,
      7, 32, 10,  // Redundant but ensures coverage
      15, 13, 22, // Connect side to back
      
      // Right side complete coverage
      2, 8, 33,
      2, 5, 8,
      5, 31, 8,
      31, 18, 8,
      31, 17, 18,
      31, 29, 17,
      29, 16, 17,
      18, 17, 19,
      8, 18, 11,
      11, 18, 19,
      8, 11, 33,  // Redundant but ensures coverage
      19, 17, 23, // Connect side to back
      
      // Back section
      9, 35, 20,
      9, 20, 26,
      9, 36, 35,
      9, 21, 36,
      9, 26, 21,
      20, 22, 26,
      21, 26, 23,
      22, 24, 26,
      23, 26, 25,
      24, 27, 26,
      25, 26, 27,
      20, 35, 22, // Fill back gap
      21, 23, 36, // Fill back gap
      
      // Side to back connections - left
      15, 10, 35,
      15, 35, 20,
      15, 20, 22,
      15, 22, 13,
      13, 22, 24,
      13, 24, 12,
      12, 24, 27,
      10, 35, 20, // Extra connection
      
      // Side to back connections - right
      19, 36, 11,
      19, 21, 36,
      19, 23, 21,
      19, 17, 23,
      17, 25, 23,
      17, 16, 25,
      16, 27, 25,
      11, 36, 21, // Extra connection
      
      // Bottom edge connections
      12, 27, 16,
      12, 16, 28, // Connect bottom front-left
      16, 29, 28, // Connect bottom front
      28, 29, 3,  // Connect bottom to hairline
      
      // Additional gap filling triangles
      7, 34, 32,  // Top-front-left
      8, 34, 33,  // Top-front-right
      7, 6, 34,   // Crown-front-left
      8, 6, 34,   // Crown-front-right
      15, 9, 20,  // Back-side-left
      19, 9, 21,  // Back-side-right
      24, 25, 27, // Back bottom
      12, 28, 24, // Left bottom corner
      16, 25, 29, // Right bottom corner
      
      // NEW TOP CAP TRIANGLES - Complete crown coverage
      // Central crown connections to new vertices
      6, 37, 7,   // Crown to left mid-top
      6, 38, 8,   // Crown to right mid-top
      6, 39, 9,   // Crown to left back-top
      6, 40, 9,   // Crown to right back-top
      6, 41, 9,   // Crown to center back-top
      
      // Connect new top vertices to form complete cap
      37, 10, 7,  // Left mid-top connections
      37, 15, 10, // Left mid-top to side
      38, 8, 11,  // Right mid-top connections
      38, 11, 19, // Right mid-top to side
      
      // Back-top connections
      39, 35, 10, // Left back-top to existing
      39, 10, 37, // Left back-top to mid
      39, 41, 35, // Left back-top to center
      40, 11, 36, // Right back-top to existing
      40, 38, 11, // Right back-top to mid
      40, 36, 41, // Right back-top to center
      
      // Fill remaining top gaps
      41, 35, 36, // Center back-top triangle
      41, 39, 40, // Back-top center triangle
      6, 35, 39,  // Crown to back corners
      6, 40, 36,  // Crown to back corners
      
      // Ensure complete radial coverage from crown
      6, 37, 34,  // Crown to front via left
      6, 34, 38,  // Crown to front via right
      37, 34, 32, // Left mid to front
      38, 33, 34, // Right mid to front
      37, 32, 10, // Complete left quadrant
      38, 11, 33, // Complete right quadrant
      
      // Final connections to eliminate any holes
      37, 6, 38,  // Top center triangle
      39, 6, 41,  // Back-top to crown
      40, 41, 6,  // Back-top to crown
      15, 37, 20, // Side to back via top
      19, 38, 21, // Side to back via top
      20, 39, 9,  // Back corner to crown
      21, 9, 40,  // Back corner to crown
      
      // Additional triangles to close back-top gap
      39, 40, 9,  // Close back-top triangle completely
      37, 39, 6,  // Left side to back-top through crown
      38, 40, 6,  // Right side to back-top through crown
      37, 38, 39, // Connect mid-tops to back-left
      38, 40, 37, // Connect mid-tops to back-right
      39, 40, 41, // Ensure back-top is fully closed
      
      // Reversed winding order for visibility from other angles
      9, 40, 39,  // Reverse: Close back-top triangle
      6, 39, 37,  // Reverse: Left side to back-top
      6, 40, 38,  // Reverse: Right side to back-top
      39, 38, 37, // Reverse: mid-tops to back-left
      37, 40, 38, // Reverse: mid-tops to back-right
      41, 40, 39, // Reverse: back-top fully closed
      9, 39, 40,  // Additional reverse for back coverage
      9, 41, 40,  // Connect back crown to back-top
      9, 39, 41,  // Connect back crown to back vertices
    ]);
    
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    geometry.computeVertexNormals();
    
    return geometry;
  }, []);
  
  // Walking animation - optimized with frame skipping
  useFrame((state) => {
    // Skip animation if camera is being moved
    if (isInteracting) return;
    
    const t = state.clock.getElapsedTime();
    
    // Body bob - add to base position instead of replacing it
    if (groupRef.current) {
      groupRef.current.position.y = 0.2 + Math.sin(t * 8) * 0.02;
    }
    
    // Torso slight rotation
    if (torsoRef.current) {
      torsoRef.current.rotation.y = Math.sin(t * 4) * 0.05;
    }
    
    // Leg movement with proper pivot
    if (leftLegGroupRef.current) {
      leftLegGroupRef.current.rotation.x = Math.sin(t * 4) * 0.3;
    }
    if (rightLegGroupRef.current) {
      rightLegGroupRef.current.rotation.x = Math.sin(t * 4 + Math.PI) * 0.3;
    }
    
    // Arm swing with proper pivot
    if (leftArmGroupRef.current) {
      leftArmGroupRef.current.rotation.x = Math.sin(t * 4 + Math.PI) * 0.2;
      leftArmGroupRef.current.rotation.z = -0.3 + Math.sin(t * 4 + Math.PI) * 0.05;
    }
    if (rightArmGroupRef.current) {
      rightArmGroupRef.current.rotation.x = Math.sin(t * 4) * 0.2;
      rightArmGroupRef.current.rotation.z = 0.3 + Math.sin(t * 4) * 0.05;
    }
  });

  // Create egg-shaped head geometry
  const headGeometry = useMemo(() => {
    const geometry = new THREE.SphereGeometry(0.65, 32, 32);
    const positions = geometry.attributes.position;
    for (let i = 0; i < positions.count; i++) {
      const y = positions.getY(i);
      // Subtle taper at bottom
      if (y < -0.2) {
        const scale = 1 - (Math.abs(y + 0.2) * 0.15);
        positions.setX(i, positions.getX(i) * scale);
        positions.setZ(i, positions.getZ(i) * scale);
      }
    }
    geometry.computeVertexNormals();
    return geometry;
  }, []);

  return (
    <group ref={groupRef} position={[0, 0.2, 0]}>
      <group ref={torsoRef}>
        {/* Head with skin-toned outline */}
        <group position={[0, 0.95, 0]}>
          <mesh geometry={headGeometry} castShadow>
            <meshToonMaterial color="#FFDBAC" />
          </mesh>
          <OutlineMesh geometry={headGeometry} color="#FFE4C4" scale={1.02} />
          
          {/* Soft hair shadow under fringe */}
          <mesh position={[0, 0.25, 0.5]} scale={[1.2, 0.6, 0.3]} castShadow={false} receiveShadow={false}>
            <sphereGeometry args={[0.3, 6, 4]} />
            <meshBasicMaterial color="#F5C3A1" opacity={0.5} transparent />
          </mesh>
          
          {/* Eyes - Simple 3D style (less bulging) */}
          <group position={[0, -0.15, 0]}>
            {/* Left eye */}
            <group position={[-0.22, 0, 0.55]} rotation={[0, -0.12, 0]}>
              {/* White of eye - partial sphere */}
              <mesh scale={[0.9, 1.2, 0.6]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.12, 6, 4, 0, Math.PI * 0.8, 0, Math.PI * 0.8]} />
                <meshBasicMaterial color="#FFFFFF" />
              </mesh>
              {/* Iris - smaller sphere */}
              <mesh position={[0, 0, 0.04]} scale={[0.9, 1.2, 0.8]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.07, 6, 4, 0, Math.PI * 0.7, 0, Math.PI * 0.7]} />
                <meshBasicMaterial color="#8B6F47" />
              </mesh>
              {/* Pupil - black sphere */}
              <mesh position={[0, 0, 0.06]} scale={[0.9, 1.1, 0.9]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.035, 4, 4]} />
                <meshBasicMaterial color="#000000" />
              </mesh>
              {/* Single highlight */}
              <mesh position={[-0.015, 0.015, 0.07]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.018, 3, 3]} />
                <meshBasicMaterial color="#FFFFFF" />
              </mesh>
            </group>
            
            {/* Right eye */}
            <group position={[0.22, 0, 0.55]} rotation={[0, 0.12, 0]}>
              {/* White of eye - partial sphere */}
              <mesh scale={[0.9, 1.2, 0.6]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.12, 6, 4, 0, Math.PI * 0.8, 0, Math.PI * 0.8]} />
                <meshBasicMaterial color="#FFFFFF" />
              </mesh>
              {/* Iris - smaller sphere */}
              <mesh position={[0, 0, 0.04]} scale={[0.9, 1.2, 0.8]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.07, 6, 4, 0, Math.PI * 0.7, 0, Math.PI * 0.7]} />
                <meshBasicMaterial color="#8B6F47" />
              </mesh>
              {/* Pupil - black sphere */}
              <mesh position={[0, 0, 0.06]} scale={[0.9, 1.1, 0.9]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.035, 4, 4]} />
                <meshBasicMaterial color="#000000" />
              </mesh>
              {/* Single highlight */}
              <mesh position={[0.015, 0.015, 0.07]} castShadow={false} receiveShadow={false}>
                <sphereGeometry args={[0.018, 3, 3]} />
                <meshBasicMaterial color="#FFFFFF" />
              </mesh>
            </group>
          </group>
          
          {/* 3D Eyebrows - Low-poly style */}
          {/* Left eyebrow */}
          <group position={[-0.25, 0.15, 0.57]} rotation={[0, -0.1, -0.15]}>
            <mesh scale={[0.15, 0.05, 0.04]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshPhongMaterial color="#2D1810" flatShading={true} />
            </mesh>
            {/* Inner taper */}
            <mesh position={[0.06, 0, 0]} scale={[0.1, 0.04, 0.035]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshPhongMaterial color="#2D1810" flatShading={true} />
            </mesh>
            {/* Outer taper */}
            <mesh position={[-0.06, -0.01, 0]} scale={[0.1, 0.035, 0.035]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshPhongMaterial color="#2D1810" flatShading={true} />
            </mesh>
          </group>
          
          {/* Right eyebrow */}
          <group position={[0.25, 0.15, 0.57]} rotation={[0, 0.1, 0.15]}>
            <mesh scale={[0.15, 0.05, 0.04]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshPhongMaterial color="#2D1810" flatShading={true} />
            </mesh>
            {/* Inner taper */}
            <mesh position={[-0.06, 0, 0]} scale={[0.1, 0.04, 0.035]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshPhongMaterial color="#2D1810" flatShading={true} />
            </mesh>
            {/* Outer taper */}
            <mesh position={[0.06, -0.01, 0]} scale={[0.1, 0.035, 0.035]}>
              <boxGeometry args={[1, 1, 1]} />
              <meshPhongMaterial color="#2D1810" flatShading={true} />
            </mesh>
          </group>
          
          {/* Nose - Smaller, positioned higher */}
          <mesh position={[0, -0.3, 0.56]} rotation={[-0.2, 0, 0]}>
            <coneGeometry args={[0.04, 0.05, 3]} />
            <meshBasicMaterial color="#FF8C42" />
          </mesh>
          
          {/* Mouth - Higher position, simple arc */}
          <mesh position={[0, -0.38, 0.54]} rotation={[0, 0, Math.PI]} scale={[0.5, 0.3, 1]}>
            <torusGeometry args={[0.06, 0.008, 8, 16, Math.PI]} />
            <meshBasicMaterial color="#8B4513" />
          </mesh>
          
          {/* Soft curved blush matching face */}
          <mesh position={[-0.38, -0.2, 0.44]} castShadow={false} receiveShadow={false}>
            <sphereGeometry args={[0.10, 4, 4]} />
            <meshBasicMaterial color="#FFB6C1" opacity={0.35} transparent />
          </mesh>
          <mesh position={[0.38, -0.2, 0.44]} castShadow={false} receiveShadow={false}>
            <sphereGeometry args={[0.10, 4, 4]} />
            <meshBasicMaterial color="#FFB6C1" opacity={0.35} transparent />
          </mesh>
        </group>
        
        {/* Hair - Low-Poly Faceted Style */}
        <group position={[0, 1.08, 0]}>
          {/* Main low-poly hair mesh */}
          <mesh geometry={hairGeometry}>
            <meshPhongMaterial 
              color="#5A3C28" 
              flatShading={true}
              shininess={5}
              side={THREE.DoubleSide}
            />
          </mesh>
          
          {/* Inner layer for depth (slightly smaller) */}
          <mesh geometry={hairGeometry} scale={[0.98, 0.98, 0.98]} position={[0, -0.01, 0]}>
            <meshPhongMaterial 
              color="#4D3319" 
              flatShading={true}
              shininess={3}
              side={THREE.DoubleSide}
            />
          </mesh>
        </group>
        
        {/* Neck with skin crescent */}
        <group position={[0, 0.4, 0]}>
          <mesh>
            <cylinderGeometry args={[0.12, 0.15, 0.15, 16]} />
            <meshToonMaterial color="#FFDBAC" />
          </mesh>
          {/* Shadow under chin */}
          <mesh position={[0, 0.08, 0.1]} rotation={[0.3, 0, 0]}>
            <torusGeometry args={[0.12, 0.02, 8, 16, Math.PI]} />
            <meshBasicMaterial color="#FFD4A3" />
          </mesh>
        </group>
        
        {/* Collar - Separate ring with thickness */}
        <mesh position={[0, 0.25, 0]}>
          <cylinderGeometry args={[0.16, 0.22, 0.08, 16]} />
          <meshToonMaterial color="#4B7F52" />
        </mesh>
        
        {/* Body - Soft cylinder */}
        <group position={[0, -0.1, 0]}>
          <RoundedBox args={[0.75, 0.65, 0.5]} radius={0.1} smoothness={4} castShadow>
            <meshToonMaterial color="#FFFFFF" />
          </RoundedBox>
          
          {/* Vertical stripes - tighter spacing */}
          {[-0.24, -0.12, 0, 0.12, 0.24].map((x, i) => (
            <mesh key={i} position={[x, 0, 0.26]} rotation={[0, Math.atan2(x, 0.25), 0]}>
              <planeGeometry args={[0.08, 0.6]} />
              <meshBasicMaterial color={i % 2 === 0 ? "#FFE66D" : "#FFFFFF"} />
            </mesh>
          ))}
          
          {/* Green placket */}
          <mesh position={[0, 0, 0.27]}>
            <boxGeometry args={[0.1, 0.55, 0.02]} />
            <meshBasicMaterial color="#4B7F52" />
          </mesh>
          
          {/* Round buttons */}
          {[0.15, 0, -0.15].map((y, i) => (
            <mesh key={i} position={[0, y, 0.28]}>
              <cylinderGeometry args={[0.03, 0.03, 0.02, 16]} />
              <meshToonMaterial color="#4B7F52" />
            </mesh>
          ))}
        </group>
      </group>
      
      {/* Arms with proper pivot points */}
      {/* Left arm group */}
      <group ref={leftArmGroupRef} position={[-0.38, 0.15, 0]}>
        <mesh position={[-0.07, -0.2, 0]}>
          <capsuleGeometry args={[0.1, 0.35, 8, 16]} />
          <meshToonMaterial color="#FFFFFF" />
        </mesh>
        {/* Left cuff */}
        <mesh position={[-0.07, -0.35, 0]}>
          <cylinderGeometry args={[0.11, 0.11, 0.08, 16]} />
          <meshToonMaterial color="#4B7F52" />
        </mesh>
        {/* Left hand */}
        <mesh position={[-0.07, -0.42, 0]}>
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshToonMaterial color="#FFDBAC" />
        </mesh>
        {/* Inner gradient */}
        <mesh position={[-0.07, -0.42, 0.08]}>
          <circleGeometry args={[0.08, 16]} />
          <meshBasicMaterial color="#F5C3A1" opacity={0.3} transparent />
        </mesh>
      </group>
      
      {/* Right arm group */}
      <group ref={rightArmGroupRef} position={[0.38, 0.15, 0]}>
        <mesh position={[0.07, -0.2, 0]}>
          <capsuleGeometry args={[0.1, 0.35, 8, 16]} />
          <meshToonMaterial color="#FFFFFF" />
        </mesh>
        {/* Right cuff */}
        <mesh position={[0.07, -0.35, 0]}>
          <cylinderGeometry args={[0.11, 0.11, 0.08, 16]} />
          <meshToonMaterial color="#4B7F52" />
        </mesh>
        {/* Right hand */}
        <mesh position={[0.07, -0.42, 0]}>
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshToonMaterial color="#FFDBAC" />
        </mesh>
        {/* Inner gradient */}
        <mesh position={[0.07, -0.42, 0.08]}>
          <circleGeometry args={[0.08, 16]} />
          <meshBasicMaterial color="#F5C3A1" opacity={0.3} transparent />
        </mesh>
      </group>
      
      {/* Legs with proper pivot points */}
      {/* Left leg group */}
      <group ref={leftLegGroupRef} position={[-0.15, -0.4, 0]}>
        <mesh position={[0, -0.2, 0]}>
          <cylinderGeometry args={[0.13, 0.12, 0.45, 16]} />
          <meshToonMaterial color="#4682B4" />
        </mesh>
        {/* Left shoe */}
        <group position={[0, -0.5, 0.05]}>
          <RoundedBox args={[0.2, 0.1, 0.28]} radius={0.05} smoothness={4}>
            <meshToonMaterial color="#FF7F50" />
          </RoundedBox>
          {/* Toe */}
          <mesh position={[0, 0, 0.1]} scale={[1, 0.8, 1]}>
            <sphereGeometry args={[0.1, 16, 16, 0, Math.PI, 0, Math.PI / 2]} />
            <meshToonMaterial color="#FF7F50" />
          </mesh>
          {/* Sole */}
          <mesh position={[0, -0.05, 0]}>
            <boxGeometry args={[0.2, 0.02, 0.28]} />
            <meshBasicMaterial color="#CD5C5C" />
          </mesh>
        </group>
      </group>
      
      {/* Right leg group */}
      <group ref={rightLegGroupRef} position={[0.15, -0.4, 0]}>
        <mesh position={[0, -0.2, 0]}>
          <cylinderGeometry args={[0.13, 0.12, 0.45, 16]} />
          <meshToonMaterial color="#4682B4" />
        </mesh>
        {/* Right shoe */}
        <group position={[0, -0.5, 0.05]}>
          <RoundedBox args={[0.2, 0.1, 0.28]} radius={0.05} smoothness={4}>
            <meshToonMaterial color="#FF7F50" />
          </RoundedBox>
          {/* Toe */}
          <mesh position={[0, 0, 0.1]} scale={[1, 0.8, 1]}>
            <sphereGeometry args={[0.1, 16, 16, 0, Math.PI, 0, Math.PI / 2]} />
            <meshToonMaterial color="#FF7F50" />
          </mesh>
          {/* Sole */}
          <mesh position={[0, -0.05, 0]}>
            <boxGeometry args={[0.2, 0.02, 0.28]} />
            <meshBasicMaterial color="#CD5C5C" />
          </mesh>
        </group>
      </group>
      
    </group>
  );
}

// Instanced grass system that moves with segments
function InstancedGrassSegment({ seed = 1 }) {
  const meshRef = useRef();
  const tempObject = useMemo(() => new THREE.Object3D(), []);
  
  // Static grass with deterministic positions based on seed
  const { positions, scales, rotations, windOffsets } = useMemo(() => {
    // Deterministic pseudo-random function based on seed
    const pseudoRandom = (index, offset = 0) => {
      const x = Math.sin(seed * 12.9898 + index * 78.233 + offset * 23.456) * 43758.5453;
      return x - Math.floor(x);
    };

    const clumpCount = 80; // Restored for better visual density
    const bladesPerClump = 2; // Restored blade count
    const positions = [];
    const scales = [];
    const rotations = [];
    const windOffsets = [];
    
    for (let i = 0; i < clumpCount; i++) {
      // Base position for clump using seed-based random
      const baseX = (pseudoRandom(i, 1) - 0.5) * 35;
      const baseZ = (pseudoRandom(i, 2) - 0.5) * 80;
      
      // Skip path - balanced clearance
      if (Math.abs(baseX) < 1.8) continue;
      
      // Create tight clump with slight offsets
      for (let j = 0; j < bladesPerClump; j++) {
        const offsetX = baseX + (pseudoRandom(i * 10 + j, 3) - 0.5) * 0.15;
        const offsetZ = baseZ + (pseudoRandom(i * 10 + j, 4) - 0.5) * 0.15;
        
        // Double-check final position isn't on road
        if (Math.abs(offsetX) < 1.3) continue;
        
        positions.push([offsetX, -0.82, offsetZ]);
        scales.push([0.35, 0.9, 0.35]);
        rotations.push(pseudoRandom(i * 10 + j, 5) * Math.PI * 2);
        // Deterministic "frozen" wind effect for each blade
        windOffsets.push((pseudoRandom(i * 10 + j, 6) - 0.5) * 0.08);
      }
    }
    
    // Ensure minimum grass coverage if too many were skipped
    if (positions.length < 100) {
      for (let i = 0; i < 50; i++) {
        const angle = (i / 50) * Math.PI * 2;
        const radius = 4 + pseudoRandom(i + 1000, 7) * 8;
        const x = Math.cos(angle) * radius;
        const z = (pseudoRandom(i + 1000, 8) - 0.5) * 60;
        
        if (Math.abs(x) > 1.5) {
          positions.push([x, -0.82, z]);
          scales.push([0.35, 0.9, 0.35]);
          rotations.push(pseudoRandom(i + 1000, 9) * Math.PI * 2);
          windOffsets.push((pseudoRandom(i + 1000, 10) - 0.5) * 0.08);
        }
      }
    }
    
    return { positions, scales, rotations, windOffsets };
  }, [seed]);
  
  // Set up instances once with static rotations
  React.useEffect(() => {
    if (!meshRef.current) return;
    
    const mesh = meshRef.current;
    
    positions.forEach((pos, i) => {
      tempObject.position.set(...pos);
      tempObject.scale.set(...scales[i]);
      tempObject.rotation.y = rotations[i];
      // Apply static "wind" tilt for variety
      tempObject.rotation.z = windOffsets[i];
      tempObject.updateMatrix();
      mesh.setMatrixAt(i, tempObject.matrix);
    });
    
    mesh.instanceMatrix.needsUpdate = true;
  }, [positions, scales, rotations, windOffsets, tempObject]);
  
  return (
    <instancedMesh ref={meshRef} args={[null, null, Math.max(positions.length, 1)]} frustumCulled={false} castShadow={false} receiveShadow={false}>
      <coneGeometry args={[0.06, 0.8, 3]} />
      <meshToonMaterial color="#4CAF50" />
    </instancedMesh>
  );
}

// Infinite terrain with hills and forest
function InfiniteForest() {
  const segment1Ref = useRef();
  const segment2Ref = useRef();
  const segment3Ref = useRef();
  const { isInteracting } = usePerformance();
  
  useFrame((_, delta) => {
    // Pause scrolling during camera interaction
    if (isInteracting) return;
    
    // Use deltaTime for smooth frame-independent movement
    const speed = 1.5;
    const segmentLength = 60;
    const movement = speed * delta; // Smooth movement based on actual frame time
    
    const moveSegment = (segment) => {
      if (!segment) return;
      segment.position.z -= movement;
      
      if (segment.position.z < -90) {
        segment.position.z += segmentLength * 3;
      }
    };
    
    moveSegment(segment1Ref.current);
    moveSegment(segment2Ref.current);
    moveSegment(segment3Ref.current);
  });
  
  
  // Low-poly tree component - simplified without fade
  const Tree = ({ position, scale = 1, type = 'pine' }) => {
    if (type === 'pine') {
      return (
        <group position={position} scale={scale}>
          {/* Tree trunk */}
          <mesh position={[0, 0.3, 0]}>
            <cylinderGeometry args={[0.15, 0.2, 0.6, 6]} />
            <meshToonMaterial color="#8B4513" side={THREE.DoubleSide} />
          </mesh>
          {/* Tree foliage - layered cones with double-sided rendering */}
          <mesh position={[0, 0.8, 0]}>
            <coneGeometry args={[0.6, 0.8, 5]} />
            <meshToonMaterial color="#228B22" side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0, 1.2, 0]}>
            <coneGeometry args={[0.5, 0.7, 5]} />
            <meshToonMaterial color="#2E8B57" side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0, 1.5, 0]}>
            <coneGeometry args={[0.3, 0.5, 5]} />
            <meshToonMaterial color="#3CB371" side={THREE.DoubleSide} />
          </mesh>
        </group>
      );
    } else {
      // Round tree
      return (
        <group position={position} scale={scale}>
          {/* Tree trunk */}
          <mesh position={[0, 0.3, 0]}>
            <cylinderGeometry args={[0.12, 0.15, 0.6, 6]} />
            <meshToonMaterial color="#8B4513" side={THREE.DoubleSide} />
          </mesh>
          {/* Round foliage with double-sided rendering */}
          <mesh position={[0, 1, 0]}>
            <sphereGeometry args={[0.7, 6, 5]} />
            <meshToonMaterial color="#32CD32" side={THREE.DoubleSide} />
          </mesh>
          {/* Fruits/flowers */}
          {[...Array(3)].map((_, i) => (
            <mesh key={i} position={[
              Math.cos(i * 2) * 0.5, 
              0.9 + Math.sin(i * 3) * 0.2, 
              Math.sin(i * 2) * 0.5
            ]}>
              <sphereGeometry args={[0.08, 6, 6]} />
              <meshToonMaterial color={i % 2 ? "#FF69B4" : "#FFB6C1"} side={THREE.DoubleSide} />
            </mesh>
          ))}
        </group>
      );
    }
  };
  
  // Bush component
  const Bush = ({ position, scale = 1 }) => (
    <group position={position} scale={scale}>
      <mesh>
        <sphereGeometry args={[0.4, 6, 5]} />
        <meshToonMaterial color="#228B22" />
      </mesh>
      {/* Berries */}
      {[...Array(2)].map((_, i) => (
        <mesh key={i} position={[
          Math.cos(i * 3) * 0.3,
          Math.sin(i * 2) * 0.1,
          Math.sin(i * 3) * 0.3
        ]}>
          <sphereGeometry args={[0.05, 4, 4]} />
          <meshToonMaterial color="#DC143C" />
        </mesh>
      ))}
    </group>
  );
  
  
  // Bunny component - fixed animation without glitching
  const Bunny = ({ position }) => {
    const groupRef = useRef();
    const { isInteracting } = usePerformance();
    const baseY = position[1];
    const frameCount = useRef(0);
    
    useFrame((state) => {
      if (groupRef.current && !isInteracting) {
        frameCount.current++;
        if (frameCount.current % 2 !== 0) return; // Skip every other frame
        const t = state.clock.getElapsedTime();
        const hop = Math.max(0, Math.sin(t * 1.5)) * 0.1;
        groupRef.current.position.y = baseY + hop;
      }
    });
    
    return (
      <group ref={groupRef} position={position}>
        {/* Body */}
        <mesh castShadow={false} receiveShadow={false}>
          <sphereGeometry args={[0.15, 5, 4]} />
          <meshToonMaterial color="#F5F5DC" />
        </mesh>
        {/* Head */}
        <mesh position={[0, 0.12, 0.1]} castShadow={false} receiveShadow={false}>
          <sphereGeometry args={[0.1, 4, 4]} />
          <meshToonMaterial color="#F5F5DC" />
        </mesh>
        {/* Ears */}
        <mesh position={[-0.04, 0.2, 0.08]} castShadow={false} receiveShadow={false}>
          <cylinderGeometry args={[0.02, 0.02, 0.1, 3]} />
          <meshToonMaterial color="#F5F5DC" />
        </mesh>
        <mesh position={[0.04, 0.2, 0.08]} castShadow={false} receiveShadow={false}>
          <cylinderGeometry args={[0.02, 0.02, 0.1, 3]} />
          <meshToonMaterial color="#F5F5DC" />
        </mesh>
        {/* Tail */}
        <mesh position={[0, 0.05, -0.15]} castShadow={false} receiveShadow={false}>
          <sphereGeometry args={[0.05, 3, 3]} />
          <meshToonMaterial color="#FFFFFF" />
        </mesh>
      </group>
    );
  };

  // Butterfly component - floats above flowers
  const Butterfly = ({ position }) => {
    const groupRef = useRef();
    const { isInteracting } = usePerformance();
    const frameCount = useRef(0);
    
    useFrame((state) => {
      if (groupRef.current && !isInteracting) {
        frameCount.current++;
        if (frameCount.current % 2 !== 0) return; // Skip every other frame
        const t = state.clock.getElapsedTime();
        groupRef.current.position.y = position[1] + Math.sin(t * 0.8) * 0.1;
        groupRef.current.position.x = position[0] + Math.cos(t * 0.5) * 0.2;
        groupRef.current.rotation.z = Math.sin(t * 5) * 0.2;
      }
    });
    
    return (
      <group ref={groupRef} position={position}>
        {/* Body */}
        <mesh>
          <cylinderGeometry args={[0.01, 0.01, 0.06, 4]} />
          <meshToonMaterial color="#2C2C2C" />
        </mesh>
        {/* Wings */}
        <mesh position={[0.03, 0, 0]}>
          <circleGeometry args={[0.04, 6]} />
          <meshToonMaterial color="#FFA500" side={THREE.DoubleSide} />
        </mesh>
        <mesh position={[-0.03, 0, 0]}>
          <circleGeometry args={[0.04, 6]} />
          <meshToonMaterial color="#FF6347" side={THREE.DoubleSide} />
        </mesh>
      </group>
    );
  };
  
  // River component - no transparency for performance
  const River = ({ position }) => (
    <mesh position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[1.5, 30]} />
      <meshToonMaterial color="#4682B4" />
    </mesh>
  );
  
  // Rock variations component
  const Rock = ({ position, scale, type }) => {
    if (type === 0) {
      // Round rock
      return (
        <mesh position={position} scale={scale}>
          <sphereGeometry args={[1, 8, 6]} />
          <meshToonMaterial color="#808080" />
        </mesh>
      );
    } else if (type === 1) {
      // Jagged rock
      return (
        <mesh position={position} scale={[scale * 1.5, scale * 2, scale]}>
          <tetrahedronGeometry args={[1, 0]} />
          <meshToonMaterial color="#696969" />
        </mesh>
      );
    } else {
      // Flat rock
      return (
        <mesh position={position} scale={[scale * 2, scale * 0.5, scale * 1.5]}>
          <boxGeometry args={[1, 1, 1]} />
          <meshToonMaterial color="#A9A9A9" />
        </mesh>
      );
    }
  };
  
  // Flower component with variations
  const Flower = ({ position, type = 0, scale = 1 }) => {
    const colors = ['#FFB6C1', '#DDA0DD', '#FF69B4', '#FFD700', '#87CEEB'];
    const petalCount = type === 0 ? 5 : type === 1 ? 6 : 8;
    
    return (
      <group position={position} scale={scale}>
        {/* Stem */}
        <mesh position={[0, 0.1, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.2, 4]} />
          <meshToonMaterial color="#228B22" />
        </mesh>
        {/* Petals */}
        {[...Array(petalCount)].map((_, i) => {
          const angle = (i / petalCount) * Math.PI * 2;
          return (
            <mesh key={i} position={[
              Math.cos(angle) * 0.08,
              0.2,
              Math.sin(angle) * 0.08
            ]} rotation={[0, 0, angle]}>
              <sphereGeometry args={[0.04, 4, 3]} />
              <meshToonMaterial color={colors[type % colors.length]} />
            </mesh>
          );
        })}
        {/* Center */}
        <mesh position={[0, 0.2, 0]}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshToonMaterial color="#FFD700" />
        </mesh>
      </group>
    );
  };
  
  // Fern component
  const Fern = ({ position, scale = 1 }) => (
    <group position={position} scale={scale}>
      {[...Array(5)].map((_, i) => (
        <mesh key={i} position={[
          (i - 2) * 0.05,
          0.05 + i * 0.02,
          0
        ]} rotation={[0, 0, (i - 2) * 0.2]}>
          <coneGeometry args={[0.06 - i * 0.01, 0.15, 4]} />
          <meshToonMaterial color="#2E8B57" />
        </mesh>
      ))}
    </group>
  );
  
  // Cattail component - green only, no brown
  const Cattail = ({ position }) => (
    <group position={position}>
      {/* Green stalk only */}
      <mesh position={[0, 0.4, 0]}>
        <cylinderGeometry args={[0.015, 0.02, 0.8, 4]} />
        <meshToonMaterial color="#4F7942" />
      </mesh>
      {/* Green top instead of brown */}
      <mesh position={[0, 0.75, 0]}>
        <sphereGeometry args={[0.03, 4, 4]} />
        <meshToonMaterial color="#2E8B57" />
      </mesh>
    </group>
  );
  
  // Lily pad component - green only
  const LilyPad = ({ position }) => (
    <group position={position}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.15, 8]} />
        <meshToonMaterial color="#2E8B57" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
  
  const TerrainSegment = ({ seed = 0 }) => {
    // Generate trees - only on right side as backdrop
    const treePositions = useMemo(() => {
      const positions = [];
      
      // Right side trees with deterministic positioning based on seed
      for (let i = 0; i < 20; i++) {
        // Use deterministic pseudo-random values based on index and seed
        const xOffset = ((i * 13 + seed * 7) % 10) / 20;
        const zOffset = ((i * 17 + seed * 11) % 20) / 10;
        const scaleOffset = ((i * 19 + seed * 13) % 10) / 20;
        
        positions.push({
          x: 3.0 + (i % 4) * 1.5 + xOffset, // Deterministic x variety
          z: -50 + i * 6 + zOffset, // Deterministic z spacing
          scale: 2.0 + scaleOffset, // Deterministic scale variety
          type: i % 2 === 0 ? 'pine' : 'round' // 50/50 distribution of tree types
        });
      }
      
      return positions;
    }, [seed]);
    
    const bushPositions = useMemo(() => {
      const positions = [];
      // Fewer bushes
      for (let i = 0; i < 10; i++) {
        const x = -2.2 - Math.abs(Math.sin(seed * 2 + i)) * 2.5;
        const z = -45 + i * 9;
        positions.push({
          x: x,
          z: z,
          scale: 0.6
        });
      }
      return positions;
    }, [seed]);
    
    // Rock formations - in grass areas only, avoiding river and trees
    const rockPositions = useMemo(() => {
      const positions = [];
      for (let i = 0; i < 6; i++) {
        let x;
        if (i % 2 === 0) {
          // Left side grass (between road and river)
          x = -2.2 - Math.abs(Math.sin(seed + i)) * 1.3; // -2.2 to -3.5
        } else {
          // Right side grass (between road and trees)
          x = 1.8 + Math.abs(Math.sin(seed + i)) * 0.8; // 1.8 to 2.6
        }
        const z = -45 + i * 15;
        positions.push({
          x: x,
          z: z,
          scale: 0.4,
          type: i % 3
        });
      }
      return positions;
    }, [seed]);
    
    return (
      <group>
        {/* Large solid base below terrain to block underneath view */}
        <mesh position={[0, -50.85, 0]} frustumCulled={false}>
          <boxGeometry args={[1500, 100, 1500]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        
        {/* Massive background plane to ensure green is always visible */}
        <mesh position={[0, -0.86, 0]} rotation={[-Math.PI / 2, 0, 0]} frustumCulled={false}>
          <planeGeometry args={[2000, 2000]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        
        {/* Solid green ground base - fixed position */}
        <mesh position={[0, -0.85, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow frustumCulled={false}>
          <planeGeometry args={[200, 200]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        
        {/* Vertical walls to block underneath view */}
        {/* North wall */}
        <mesh position={[0, -25, -400]}>
          <boxGeometry args={[800, 50, 1]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        {/* South wall */}
        <mesh position={[0, -25, 400]}>
          <boxGeometry args={[800, 50, 1]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        {/* East wall */}
        <mesh position={[400, -25, 0]}>
          <boxGeometry args={[1, 50, 800]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        {/* West wall */}
        <mesh position={[-400, -25, 0]}>
          <boxGeometry args={[1, 50, 800]} />
          <meshBasicMaterial color="#90EE90" />
        </mesh>
        
        {/* Dirt patches on the ground - raised to prevent z-fighting with grass */}
        {[...Array(3)].map((_, i) => (
          <mesh key={i} position={[
            1.5 + Math.abs(Math.sin(seed + i * 3)) * 0.3,  // Further left: 1.5 to 1.8
            -0.80,  // Raised from -0.83 to prevent clipping through grass
            -25 + i * 25
          ]} rotation={[-Math.PI / 2, Math.sin(seed + i) * 0.5, 0]}>
            <circleGeometry args={[0.6 + Math.sin(seed + i * 2) * 0.2, 6]} />
            <meshToonMaterial color="#8B7355" />
          </mesh>
        ))}
        
        {/* Main path on the ground - longer for continuity */}
        <mesh position={[0, -0.82, 0]} rotation={[-Math.PI / 2, 0, 0]} frustumCulled={false}>
          <planeGeometry args={[2.2, 100, 1, 50]} />
          <meshToonMaterial color="#D2B48C" />
        </mesh>
        
        {/* Rock formations */}
        {rockPositions.map((rock, i) => (
          <Rock 
            key={i}
            position={[rock.x, -0.75, rock.z]} 
            scale={rock.scale}
            type={rock.type}
          />
        ))}
        
        {/* Trees on ground */}
        {treePositions.map((tree, i) => (
          <Tree 
            key={i} 
            position={[tree.x, -0.85, tree.z]} 
            scale={tree.scale}
            type={tree.type}
          />
        ))}
        
        {/* Bushes on ground - properly grounded */}
        {bushPositions.map((bush, i) => (
          <Bush 
            key={i} 
            position={[bush.x, -0.85, bush.z]} 
            scale={bush.scale}
          />
        ))}
        
        {/* River alongside path */}
        <River position={[-5, -0.84, 0]} />
        
        {/* Rare bunny */}
        <Bunny position={[4, -0.7, -10]} />
        
        {/* Static river raised to prevent z-fighting */}
        <mesh position={[-4, -0.82, 0]} rotation={[-Math.PI / 2, 0, 0]} frustumCulled={false} renderOrder={1}>
          <planeGeometry args={[1.5, 100]} />
          <meshBasicMaterial color="#4682B4" opacity={0.9} transparent depthWrite={false} />
        </mesh>
        
        {/* Spread out left side decoration with no overlapping */}
        {/* Flowers with fixed spacing */}
        {[...Array(3)].map((_, i) => {
          // Use deterministic but spaced positions
          const offset = ((seed * 13 + i * 7) % 5) * 0.3;
          return (
            <Flower 
              key={`flower-${seed}-${i}`}
              position={[
                -2.5 - (i % 2) * 1.5 - offset,
                -0.83,
                -30 + i * 12
              ]}
              type={i % 5}
              scale={1}
            />
          );
        })}
        
        {/* Ferns with proper spacing */}
        {[...Array(2)].map((_, i) => {
          return (
            <Fern 
              key={`fern-${seed}-${i}`}
              position={[
                -3.5 - (i % 2) * 0.8,
                -0.85,
                -45 + i * 20
              ]}
              scale={1}
            />
          );
        })}
        
        {/* Cattails near water */}
        {[...Array(4)].map((_, i) => {
          const positions = [
            [-3, -0.85, -30 + i * 8],
            [-5, -0.85, -25 + i * 8],
            [-3.5, -0.85, -35 + i * 8],
            [-4.5, -0.85, -20 + i * 8]
          ];
          const pos = positions[i % 4];
          return <Cattail key={`cattail-${seed}-${i}`} position={pos} />;
        })}
        
        {/* Lily pads on water */}
        {[...Array(3)].map((_, i) => {
          const z = -35 + i * 12;
          const x = -4 + Math.sin(seed + i) * 0.3;
          return <LilyPad key={`lily-${seed}-${i}`} position={[x, -0.83, z]} />;
        })}
        
        {/* Minimal animals for ultra performance */}
        {/* Animals removed for cleaner scene */}
        
      </group>
    );
  };
  
  // Initialize segments with proper positions for seamless loop
  React.useEffect(() => {
    if (segment1Ref.current) segment1Ref.current.position.z = 30;
    if (segment2Ref.current) segment2Ref.current.position.z = -30;
    if (segment3Ref.current) segment3Ref.current.position.z = -90;
  }, []);
  
  // Use stable seeds for consistent procedural generation
  const segmentSeeds = useMemo(() => {
    // Use stable seeds to prevent regeneration and lag
    const baseSeed = 42; // Fixed base seed for consistency
    return [
      baseSeed + 7,
      baseSeed + 13,
      baseSeed + 23
    ];
  }, []); // Empty deps means this only runs once per component mount
  
  return (
    <>
      <group ref={segment1Ref}>
        <TerrainSegment seed={segmentSeeds[0]} />
        <InstancedGrassSegment seed={segmentSeeds[0] * 2} />
      </group>
      <group ref={segment2Ref}>
        <TerrainSegment seed={segmentSeeds[1]} />
        <InstancedGrassSegment seed={segmentSeeds[1] * 2} />
      </group>
      <group ref={segment3Ref}>
        <TerrainSegment seed={segmentSeeds[2]} />
        <InstancedGrassSegment seed={segmentSeeds[2] * 2} />
      </group>
      
      {/* Distant mountains - static background */}
      <group position={[0, 0, -30]}>
        {/* Far mountains */}
        <mesh position={[-15, 2, -20]} scale={[8, 6, 4]}>
          <coneGeometry args={[1, 1, 4]} />
          <meshBasicMaterial color="#9CA4B8" />
        </mesh>
        <mesh position={[18, 3, -25]} scale={[10, 8, 5]}>
          <coneGeometry args={[1, 1, 4]} />
          <meshBasicMaterial color="#A8B2C8" />
        </mesh>
        <mesh position={[0, 2.5, -22]} scale={[7, 7, 4]}>
          <coneGeometry args={[1, 1, 5]} />
          <meshBasicMaterial color="#B0B8CC" />
        </mesh>
        
      </group>
      
      {/* Simplified clouds - no transparency */}
      {[...Array(3)].map((_, i) => (
        <group key={i} position={[
          Math.sin(i * 2) * 8,
          5 + Math.sin(i * 3) * 1,
          -10 + i * 20
        ]}>
          <mesh scale={[2, 0.8, 1.5]}>
            <sphereGeometry args={[1, 4, 4]} />
            <meshBasicMaterial color="#FFFFFF" />
          </mesh>
          <mesh position={[1.2, 0, 0]} scale={[1.5, 0.7, 1.2]}>
            <sphereGeometry args={[0.8, 4, 4]} />
            <meshBasicMaterial color="#F8F8F8" />
          </mesh>
        </group>
      ))}
    </>
  );
}


// Static lighting component (no rotation to prevent shadow movement)
function StaticLighting() {
  return (
    <>
      {/* Main static light with harder shadows */}
      <directionalLight 
        color="#FFFFFF" 
        intensity={1.4}
        position={[2, 10, 5]}
        castShadow
        shadow-mapSize={[4096, 4096]}
        shadow-camera-far={50}
        shadow-camera-left={-30}
        shadow-camera-right={30}
        shadow-camera-top={30}
        shadow-camera-bottom={-30}
        shadow-bias={-0.001}
        shadow-normalBias={0.02}
      />
      
      {/* Secondary warm fill light (no shadows) */}
      <directionalLight 
        color="#FFE6CC" 
        intensity={0.5}
        position={[-4, 3, 2]}
      />
      
      {/* Back rim light (no shadows) */}
      <directionalLight 
        color="#FFFFFF" 
        intensity={0.3}
        position={[0, 2, -3]}
      />
    </>
  );
}

// Camera setup with smooth zoom control
function CameraSetup({ zoom }) {
  const { camera } = useThree();
  const initialPositionSet = useRef(false);
  
  React.useEffect(() => {
    if (camera && !initialPositionSet.current) {
      camera.position.set(-3, 2, 5);
      camera.lookAt(0, 0, 0);
      initialPositionSet.current = true;
    }
  }, [camera]);
  
  // Smooth zoom interpolation using useFrame
  useFrame(() => {
    if (camera) {
      // Lerp the zoom for smooth transitions
      const smoothingFactor = 0.12; // Adjust for faster/slower smoothing
      camera.zoom += (zoom - camera.zoom) * smoothingFactor;
      camera.updateProjectionMatrix();
    }
  });
  
  return null;
}

// Main component
function AnimalCrossingAvatar() {
  const [zoom, setZoom] = React.useState(71);
  
  // Add CSS animation for fade-in
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  
  return (
    <PerformanceProvider>
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, position: 'relative' }}>
      <Canvas
        orthographic
        camera={{ 
          zoom: zoom, 
          position: [-3, 2, 5], 
          near: 0.1, 
          far: 1000
        }}
        style={{ background: '#87CEEB' }}
        shadows={false}
        gl={{ 
          antialias: false, 
          toneMapping: THREE.NoToneMapping,
          powerPreference: "high-performance"
        }}
        onCreated={({ camera }) => {
          // Remove fog for performance
          // scene.fog = new THREE.Fog('#B0E0E6', 20, 50);
          camera.zoom = zoom;
          camera.updateProjectionMatrix();
        }}
      >
        <CameraSetup zoom={zoom} />
        
        {/* Bright ambient light for sunny day */}
        <ambientLight intensity={1.4} color="#FFF8DC" />
        
        <StaticLighting />
        
        <Character />
        <InfiniteForest />
        
        <CameraController />
      </Canvas>
      <AnimationControls />
      <CameraInstructions />
      </div>
      
      {/* Zoom slider */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px 20px',
        gap: '15px'
      }}>
        <span style={{
          fontSize: '18px',
          color: '#94a3b8',
          fontWeight: '400',
          userSelect: 'none'
        }}>−</span>
        <input 
          type="range" 
          min="71" 
          max="120" 
          value={zoom} 
          onChange={(e) => setZoom(Number(e.target.value))}
          style={{
            width: '180px',
            height: '6px',
            background: `linear-gradient(to right, #60a5fa 0%, #60a5fa ${((zoom - 71) / 49) * 100}%, #e2e8f0 ${((zoom - 71) / 49) * 100}%, #e2e8f0 100%)`,
            borderRadius: '3px',
            outline: 'none',
            WebkitAppearance: 'none',
            appearance: 'none',
            cursor: 'pointer',
            border: 'none'
          }}
        />
        <span style={{
          fontSize: '18px',
          color: '#94a3b8',
          fontWeight: '400',
          userSelect: 'none'
        }}>+</span>
      </div>
    </div>
    </PerformanceProvider>
  );
}

// Camera movement instructions
function CameraInstructions() {
  const [hasInteracted, setHasInteracted] = useState(
    sessionStorage.getItem('hasInteractedWithCamera') === 'true'
  );
  const { isInteracting } = usePerformance();
  
  // Hide instructions after first interaction in this session
  React.useEffect(() => {
    if (isInteracting && !hasInteracted) {
      sessionStorage.setItem('hasInteractedWithCamera', 'true');
      setHasInteracted(true);
    }
  }, [isInteracting, hasInteracted]);
  
  if (hasInteracted) return null;
  
  return (
    <div
      style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '12px 24px',
        background: 'rgba(0, 0, 0, 0.5)',
        color: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '14px',
        fontWeight: '500',
        zIndex: 1000,
        pointerEvents: 'none',
        animation: 'fadeIn 0.5s ease-in',
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.8 }}>
        <path d="M7 12L12 7L17 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" transform="rotate(-45 12 12)"/>
      </svg>
      <span>Drag to rotate view</span>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.8 }}>
        <path d="M7 12L12 7L17 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" transform="rotate(45 12 12)"/>
      </svg>
    </div>
  );
}

// Animation paused indicator
function AnimationControls() {
  const { isInteracting } = usePerformance();
  
  if (!isInteracting) return null;
  
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: '500',
        background: 'rgba(0, 0, 0, 0.6)',
        color: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '6px',
        backdropFilter: 'blur(10px)',
        transition: 'opacity 0.3s ease',
        zIndex: 1000,
        pointerEvents: 'none'
      }}
    >
      Paused
    </div>
  );
}

// Camera controller with mouse-hold detection for pausing
function CameraController() {
  const { setIsInteracting } = usePerformance();
  const controlsRef = useRef();
  
  useEffect(() => {
    const handlePointerDown = () => {
      setIsInteracting(true);
    };
    
    const handlePointerUp = () => {
      setIsInteracting(false);
    };
    
    // Add event listeners to the canvas
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.addEventListener('pointerdown', handlePointerDown);
      canvas.addEventListener('pointerup', handlePointerUp);
      canvas.addEventListener('pointercancel', handlePointerUp);
      canvas.addEventListener('pointerleave', handlePointerUp);
      
      return () => {
        canvas.removeEventListener('pointerdown', handlePointerDown);
        canvas.removeEventListener('pointerup', handlePointerUp);
        canvas.removeEventListener('pointercancel', handlePointerUp);
        canvas.removeEventListener('pointerleave', handlePointerUp);
      };
    }
  }, [setIsInteracting]);
  
  return (
    <OrbitControls 
      ref={controlsRef}
      enablePan={false} 
      enableZoom={false} 
      enableRotate={true}
      target={[0, 0.5, 0]}
      minPolarAngle={0}
      maxPolarAngle={Math.PI * 0.42}
      minAzimuthAngle={-Math.PI * 0.83}
      maxAzimuthAngle={-Math.PI * 0.167}
      minDistance={3}
      maxDistance={10}
    />
  );
}

export default AnimalCrossingAvatar;